/**
 * ProcessCanvas — Main canvas wrapper component.
 *
 * Wraps React Flow with:
 * - Custom node types (Step, Actor, Tool, Insight, Trigger, SectionGroup)
 * - CanvasToolbar (zoom, fit, legend)
 * - NodeActionContext for hover action buttons + staged actions + focus state
 * - AddContextSheet (modal) for typed node context
 * - Auto-zoom navigation with user interaction queuing
 * - Knowledge area clustering with proposal modal
 * - Loading, error, and empty states
 * - Non-draggable nodes with always-on dagre layout
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  ViewportPortal,
  useReactFlow,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { UseProcessCanvasReturn } from '@/hooks/useProcessCanvas';
import { useCanvasNavigation, type AgentFocusRequest } from '@/hooks/useCanvasNavigation';
import { useClusterManager, type ClusterProposal } from '@/hooks/useClusterManager';
import { useCanvasAutoSave } from '@/hooks/useCanvasAutoSave';
import { CanvasToolbar } from './CanvasToolbar';
import { CanvasLegend } from './CanvasLegend';
import { UndoPill } from './UndoPill';
import { AddContextSheet, type ContextKind } from './AddContextSheet';
import { CanvasOptionsProvider } from './CanvasOptionsContext';
import type { NodeType } from '@/lib/nodeTypes';
import { NodeActionProvider } from './NodeActionContext';
import { StagedNodeReviewer } from './StagedNodeReviewer';
import { ClusterBackground } from './ClusterBackground';
import { ClusterLabel } from './ClusterLabel';
import { ClusteringProposalModal } from './ClusteringProposalModal';
import { EmptyCanvasState } from './EmptyCanvasState';
import { Skeleton, ErrorPill } from '@/components/feedback';
import { createNodeContext } from '@/services/api';
import { StepNode } from './nodes/StepNode';
import { ActorNode } from './nodes/ActorNode';
import { ToolNode } from './nodes/ToolNode';
import { InsightNode } from './nodes/InsightNode';
import { TriggerNode } from './nodes/TriggerNode';
import { SectionGroupNode } from './nodes/SectionGroupNode';
import { KaivaEdge } from './edges/KaivaEdge';

interface ProcessCanvasProps {
  sessionId?: string;
  canvas: UseProcessCanvasReturn;
  onNodeContextSubmit?: (
    nodeId: string,
    nodeLabel: string,
    nodeType: string,
    message: string,
    kind: ContextKind,
  ) => Promise<void>;
  /** Expose navigation controls to parent */
  onNavigationReady?: (nav: {
    focusOnNode: (nodeId: string) => void;
    focusOnArea: (areaId: string) => void;
    resetView: () => void;
    handleFocusRequest: (req: AgentFocusRequest) => void;
  }) => void;
  /** Expose clustering controls to parent */
  onClusteringReady?: (clustering: {
    proposeClusters: (proposals: ClusterProposal[]) => void;
    getClusters: () => import('@shared/types.ts').CanvasCluster[];
  }) => void;
  /** Set of node IDs currently highlighted by the agent bridge */
  highlightedNodeIds?: Set<string>;
  /** Override confirm handler (wraps canvas.confirmNode with bridge event) */
  onConfirmNode?: (nodeId: string) => void;
  /** Override dismiss handler (wraps canvas.dismissNode with bridge event) */
  onDismissNode?: (nodeId: string) => void;
  /** Whether edge labels should render (driven by SessionTopbar's labels toggle). */
  showLabels?: boolean;
  /** When true, dim everything on the canvas except the active staged node and its direct neighbours. */
  focusMode?: boolean;
}

const emptySet = new Set<string>();

const nodeTypes: NodeTypes = {
  step: StepNode,
  actor: ActorNode,
  tool: ToolNode,
  insight: InsightNode,
  trigger: TriggerNode,
  sectionGroup: SectionGroupNode,
};

const edgeTypes: EdgeTypes = {
  kaiva: KaivaEdge,
};

function ProcessCanvasInner({
  sessionId,
  canvas,
  onNodeContextSubmit,
  onNavigationReady,
  onClusteringReady,
  highlightedNodeIds,
  onConfirmNode: onConfirmNodeOverride,
  onDismissNode: onDismissNodeOverride,
  showLabels = true,
  focusMode = false,
}: ProcessCanvasProps) {
  const [hiddenTypes, setHiddenTypes] = useState<Set<NodeType>>(() => new Set());
  const [activeStagedId, setActiveStagedId] = useState<string | null>(null);

  const toggleHiddenType = useCallback((type: NodeType) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isLoadingCanvas,
    canvasError,
    layoutVersion,
    confirmNode,
    dismissNode,
    addNodeContext,
    undoAction,
    undoLast,
    relayout,
  } = canvas;

  // Use overrides if provided (bridge-wrapped), otherwise use canvas defaults
  const effectiveConfirmNode = onConfirmNodeOverride ?? confirmNode;
  const effectiveDismissNode = onDismissNodeOverride ?? dismissNode;

  const { fitView } = useReactFlow();

  // Canvas navigation
  const navigation = useCanvasNavigation(nodes, edges);

  // Cluster management (initialize from persisted state if available)
  const clusterMgr = useClusterManager(canvas.initialClusters);
  const [isClusterAnimating, setIsClusterAnimating] = useState(false);

  // Auto-save canvas state on interaction changes (debounced)
  useCanvasAutoSave(
    sessionId ?? '',
    nodes,
    edges,
    clusterMgr.clusters,
    canvas.dismissedNodes,
    isLoadingCanvas || !sessionId,
  );

  // Expose navigation to parent
  useEffect(() => {
    onNavigationReady?.({
      focusOnNode: navigation.focusOnNode,
      focusOnArea: navigation.focusOnArea,
      resetView: navigation.resetView,
      handleFocusRequest: navigation.handleFocusRequest,
    });
  }, [onNavigationReady, navigation.focusOnNode, navigation.focusOnArea, navigation.resetView, navigation.handleFocusRequest]);

  // Expose clustering to parent
  useEffect(() => {
    onClusteringReady?.({
      proposeClusters: (proposals: ClusterProposal[]) => {
        clusterMgr.proposeCluster(proposals, nodes);
      },
      getClusters: () => clusterMgr.clusters,
    });
  }, [onClusteringReady, clusterMgr.proposeCluster, clusterMgr.clusters, nodes]);

  // Recalculate cluster bounds when layout changes
  useEffect(() => {
    if (layoutVersion > 0 && clusterMgr.clusters.length > 0) {
      clusterMgr.recalculateBounds(nodes);
    }
  }, [layoutVersion, nodes, clusterMgr.clusters.length, clusterMgr.recalculateBounds]);

  // Active node for the Add Context sheet
  const [activeNode, setActiveNode] = useState<{
    nodeId: string;
    label: string;
    type: string;
    description?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize to avoid unnecessary re-renders
  const stableNodeTypes = useMemo(() => nodeTypes, []);
  const stableEdgeTypes = useMemo(() => edgeTypes, []);

  // Set of IDs that should stay at full opacity in focus mode:
  // the active staged node + the other endpoints of every edge it touches.
  const focusedSet = useMemo<Set<string> | null>(() => {
    if (!focusMode || !activeStagedId) return null;
    const set = new Set<string>([activeStagedId]);
    for (const e of edges) {
      if (e.source === activeStagedId) set.add(e.target);
      if (e.target === activeStagedId) set.add(e.source);
    }
    return set;
  }, [focusMode, activeStagedId, edges]);

  const dimStyle = {
    opacity: 0.25,
    transition: 'opacity 200ms ease',
  } as const;
  const litStyle = {
    opacity: 1,
    transition: 'opacity 200ms ease',
  } as const;

  // Apply per-type visibility filter from the legend, plus focus-mode dimming.
  const displayNodes = useMemo(() => {
    const noFilter = hiddenTypes.size === 0;
    const noFocus = focusedSet === null;
    if (noFilter && noFocus) return nodes;
    return nodes.map((n) => {
      const t = (n.data?.canvasNodeType as string | undefined)?.toLowerCase();
      const hide = !noFilter && t ? hiddenTypes.has(t as NodeType) : false;
      const dimmed = !noFocus && !focusedSet!.has(n.id);
      let next = n;
      if (hide) next = { ...next, hidden: true };
      if (dimmed) next = { ...next, style: { ...(next.style ?? {}), ...dimStyle } };
      else if (!noFocus) next = { ...next, style: { ...(next.style ?? {}), ...litStyle } };
      return next;
    });
  }, [nodes, hiddenTypes, focusedSet]);

  // Apply focus-mode dimming to edges: only edges touching the active node stay lit.
  const displayEdges = useMemo(() => {
    if (focusedSet === null) return edges;
    return edges.map((e) => {
      const lit = e.source === activeStagedId || e.target === activeStagedId;
      return { ...e, style: { ...(e.style ?? {}), ...(lit ? litStyle : dimStyle) } };
    });
  }, [edges, focusedSet, activeStagedId]);

  // Ref to read current nodes without adding to effect deps
  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  // After layout changes: focus on first staged node if any, otherwise fitView all.
  // IMPORTANT: only depends on layoutVersion + fitView — NOT nodes/navigation,
  // otherwise React Flow's re-renders on viewport change cause an infinite zoom loop.
  useEffect(() => {
    if (layoutVersion > 0) {
      const timer = setTimeout(() => {
        const currentNodes = nodesRef.current;
        const firstStaged = currentNodes.find((n) => n.data.staged);
        if (firstStaged) {
          // Zoom directly to the first staged node (bypass interaction queue)
          fitView({
            nodes: [{ id: firstStaged.id }],
            duration: 300,
            padding: 0.4,
            maxZoom: 1.5,
          });
        } else {
          fitView({ duration: 400, padding: 0.3 });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [layoutVersion, fitView]);

  // Node action handler — opens the Add Context sheet.
  // Track C.3: ignore clicks while the canvas is replaying a prior turn.
  const handleNodeAction = useCallback(
    (nodeId: string, nodeLabel: string, nodeType: string) => {
      if (canvas.isReplaying) return;
      const node = nodesRef.current.find((n) => n.id === nodeId);
      setActiveNode({
        nodeId,
        label: nodeLabel,
        type: nodeType,
        description: node?.data.description,
      });
    },
    [canvas.isReplaying],
  );

  // Staged-node "Edit" routes to the same sheet
  const handleEditNode = useCallback(
    (nodeId: string) => {
      if (canvas.isReplaying) return;
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setActiveNode({
          nodeId,
          label: node.data.label,
          type: node.data.canvasNodeType,
          description: node.data.description,
        });
      }
    },
    [nodes, canvas.isReplaying],
  );

  // Submit node context — persists to the canvas + threads a chat system note.
  // Track C.2: also persists to the dedicated NodeContext table via a
  // fire-and-forget call (sessionId is the source of durability for reload).
  const handleNodeContextSubmit = useCallback(
    async (nodeId: string, kind: ContextKind, message: string) => {
      if (!activeNode || activeNode.nodeId !== nodeId) return;

      addNodeContext(nodeId, message, kind);

      if (sessionId) {
        createNodeContext(sessionId, nodeId, kind, message).catch((err) => {
          console.warn('[ProcessCanvas] NodeContext persist failed:', err);
        });
      }

      if (onNodeContextSubmit) {
        setIsSubmitting(true);
        try {
          await onNodeContextSubmit(
            activeNode.nodeId,
            activeNode.label,
            activeNode.type,
            message,
            kind,
          );
        } catch (err) {
          console.warn('[ProcessCanvas] Node context submission failed:', err);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [activeNode, onNodeContextSubmit, addNodeContext, sessionId],
  );

  // Apply cluster proposal with animation
  const handleApplyProposal = useCallback(() => {
    setIsClusterAnimating(true);
    clusterMgr.applyProposal(nodes);
    // Animation: clusters fade in after nodes settle (handled by CSS)
    setTimeout(() => setIsClusterAnimating(false), 600);
  }, [clusterMgr, nodes]);

  // Loading state
  if (isLoadingCanvas) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30 p-4">
        <Skeleton variant="card" className="max-w-xs" />
      </div>
    );
  }

  // Error state
  if (canvasError) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30 p-4">
        <div className="max-w-xs">
          <ErrorPill message={canvasError} />
        </div>
      </div>
    );
  }

  return (
    <div id="canvas-region" tabIndex={-1} className="relative h-full w-full focus:outline-none">
      <NodeActionProvider
        onNodeAction={handleNodeAction}
        activeNodeId={activeNode?.nodeId ?? null}
        onConfirmNode={effectiveConfirmNode}
        onEditNode={handleEditNode}
        onDismissNode={effectiveDismissNode}
        focusedNodeId={navigation.focusedNodeId}
        highlightedNodeIds={highlightedNodeIds ?? emptySet}
      >
        <CanvasOptionsProvider showLabels={showLabels}>
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodesDraggable={false}
          nodeTypes={stableNodeTypes}
          edgeTypes={stableEdgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          className="bg-background"
        >
          {/* Substrate dot-grid — knowledge-graph canvas tier per v1.6 spec.
              Each dot is a potential node; the persimmon i-dot is one captured. */}
          <Background
            variant={BackgroundVariant.Dots}
            gap={16}
            size={1}
            color="hsl(var(--substrate))"
            style={{ opacity: 0.65 }}
          />
          <CanvasToolbar onRelayout={relayout} />
          <CanvasLegend hiddenTypes={hiddenTypes} onToggleType={toggleHiddenType} />

          {/* Cluster rectangles + labels rendered into the transformed viewport
              so they actually pan/zoom with the nodes. ViewportPortal applies
              the same transform React Flow uses for nodes/edges, in flow-coord
              space — bounds.x/y from useClusterManager are flow coordinates so
              no math change is needed. */}
          <ViewportPortal>
            <ClusterBackground
              clusters={clusterMgr.clusters}
              isAnimating={isClusterAnimating}
            />
            {clusterMgr.clusters.map((cluster) => (
              <ClusterLabel
                key={cluster.id}
                clusterId={cluster.id}
                name={cluster.name}
                x={cluster.bounds.x}
                y={cluster.bounds.y}
                onRename={clusterMgr.renameCluster}
              />
            ))}
          </ViewportPortal>
        </ReactFlow>
        </CanvasOptionsProvider>
      </NodeActionProvider>

      {/* Undo pill — floats above the staged reviewer when an action is reversible */}
      <UndoPill action={undoAction} onUndo={undoLast} />

      {/* Staged node reviewer — floating < 1/4 > navigator */}
      <StagedNodeReviewer
        nodes={nodes}
        onConfirm={effectiveConfirmNode}
        onEdit={handleEditNode}
        onDismiss={effectiveDismissNode}
        onFocusNode={navigation.focusOnNode}
        onActiveChange={setActiveStagedId}
        disabled={canvas.isReplaying}
      />

      {/* Navigation pending toast */}
      {navigation.isNavigationPending && (
        <div className="absolute bottom-3 left-1/2 z-50 -translate-x-1/2 animate-[fadeIn_0.15s_ease-out] rounded-lg border border-border bg-white/90 px-3 py-1.5 shadow-md backdrop-blur-sm">
          <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-kaiva-coral" />
            Navigation pending...
          </p>
        </div>
      )}

      {/* Clustering proposal modal */}
      {clusterMgr.pendingProposal && (
        <ClusteringProposalModal
          proposals={clusterMgr.pendingProposal}
          onApply={handleApplyProposal}
          onDismiss={clusterMgr.dismissProposal}
        />
      )}

      {/* Add Context sheet */}
      <AnimatePresence>
        {activeNode && (
          <AddContextSheet
            key={activeNode.nodeId}
            node={{
              id: activeNode.nodeId,
              label: activeNode.label,
              type: activeNode.type,
              description: activeNode.description,
            }}
            isSubmitting={isSubmitting}
            onSubmit={handleNodeContextSubmit}
            onClose={() => setActiveNode(null)}
          />
        )}
      </AnimatePresence>

      {/* Empty state overlay when no nodes — Track E.1 listening state */}
      {nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <EmptyCanvasState />
        </div>
      )}
    </div>
  );
}

/**
 * ProcessCanvas wrapped in ReactFlowProvider.
 * The provider is required for useReactFlow() in CanvasToolbar, fitView, and navigation.
 */
export function ProcessCanvas(props: ProcessCanvasProps) {
  return (
    <ReactFlowProvider>
      <ProcessCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
